import { API_CONFIG } from '../config/api';

export interface VulnerabilityAssessment {
  highSeverity: {
    title: string;
    description: string;
    actionLink: string;
  };
  criticalAlert: {
    title: string;
    description: string;
    actionLink: string;
  };
}

// Default assessments if AI is unavailable
const DEFAULT_ASSESSMENTS: VulnerabilityAssessment = {
  highSeverity: {
    title: 'High Severity',
    description: 'Significant disparity in remote vs. on-campus well-being scores.',
    actionLink: 'View Analysis'
  },
  criticalAlert: {
    title: 'Critical Alert',
    description: 'Leadership communication gap detected in Engineering faculty.',
    actionLink: 'View Details'
  }
};

export type VulnerabilityAssessmentSource = 'ai' | 'default';

export interface GenerateVulnerabilityAssessmentsResult {
  assessments: VulnerabilityAssessment;
  source: VulnerabilityAssessmentSource;
}

/** Copy defaults so callers always get a new object reference (ensures React state updates). */
function getDefaultAssessmentsCopy(): VulnerabilityAssessment {
  return {
    highSeverity: { ...DEFAULT_ASSESSMENTS.highSeverity },
    criticalAlert: { ...DEFAULT_ASSESSMENTS.criticalAlert }
  };
}

/**
 * Ensure description values are always strings (cache may contain objects e.g. { dimension, groups }).
 * React cannot render objects as children.
 */
function descriptionToText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'dimension' in value && 'groups' in value) {
    const obj = value as { dimension: unknown; groups: unknown };
    const groups = Array.isArray(obj.groups) ? obj.groups.join(', ') : String(obj.groups);
    return `${obj.dimension} focus in groups: ${groups}.`;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function normalizeAssessment(assessment: VulnerabilityAssessment): VulnerabilityAssessment {
  return {
    highSeverity: {
      ...DEFAULT_ASSESSMENTS.highSeverity,
      ...assessment.highSeverity,
      description: descriptionToText(assessment.highSeverity?.description) || DEFAULT_ASSESSMENTS.highSeverity.description
    },
    criticalAlert: {
      ...DEFAULT_ASSESSMENTS.criticalAlert,
      ...assessment.criticalAlert,
      description: descriptionToText(assessment.criticalAlert?.description) || DEFAULT_ASSESSMENTS.criticalAlert.description
    }
  };
}

/**
 * Generates vulnerability assessments using AI
 * Falls back to default values if AI is unavailable.
 * Returns a new object reference when using defaults so React state updates.
 */
export const generateVulnerabilityAssessments = async (
  token: string,
  groupData?: any
): Promise<GenerateVulnerabilityAssessmentsResult> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/ai/vulnerability-assessment`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          groupData: groupData || {},
          assessmentType: 'systemic_risks'
        })
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data) {
        const source: VulnerabilityAssessmentSource = data.source === 'ai' ? 'ai' : 'default';
        const highDesc = data.data.highSeverityDescription ?? DEFAULT_ASSESSMENTS.highSeverity.description;
        const criticalDesc = data.data.criticalAlertDescription ?? DEFAULT_ASSESSMENTS.criticalAlert.description;
        return {
          assessments: {
            highSeverity: {
              title: 'High Severity',
              description: typeof highDesc === 'string' ? highDesc : String(highDesc),
              actionLink: 'View Analysis'
            },
            criticalAlert: {
              title: 'Critical Alert',
              description: typeof criticalDesc === 'string' ? criticalDesc : String(criticalDesc),
              actionLink: 'View Details'
            }
          },
          source
        };
      }
    }

    console.warn('AI assessment unavailable, using default values');
    return { assessments: getDefaultAssessmentsCopy(), source: 'default' };
  } catch (error) {
    console.error('Error generating vulnerability assessments:', error);
    return { assessments: getDefaultAssessmentsCopy(), source: 'default' };
  }
};

/**
 * Gets cached assessments or generates new ones.
 * Assessments are refreshed every hour.
 */
export const getVulnerabilityAssessments = async (
  token: string,
  cacheKey: string = 'vulnerability_assessments',
  groupData?: any
): Promise<VulnerabilityAssessment> => {
  // Check if we have cached assessments
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const cachedData = JSON.parse(cached);
      // Validate cache is recent (within 1 hour)
      const cacheTime = localStorage.getItem(`${cacheKey}_time`);
      if (cacheTime) {
        const age = Date.now() - parseInt(cacheTime);
        if (age < 60 * 60 * 1000) {
          return normalizeAssessment(cachedData);
        }
      }
    } catch (e) {
      console.error('Error parsing cached assessments:', e);
    }
  }

  // Generate new assessments
  const { assessments, source } = await generateVulnerabilityAssessments(token, groupData);
  const normalized = normalizeAssessment(assessments);

  // Cache the result ONLY if it's successfully generated by AI (don't cache fallback defaults)
  if (source === 'ai') {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(normalized));
      localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
    } catch (e) {
      console.error('Error caching assessments:', e);
    }
  }

  return normalized;
};

/**
 * Gets default assessments without AI
 */
export const getDefaultAssessments = (): VulnerabilityAssessment => {
  return DEFAULT_ASSESSMENTS;
};
